/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import React from "react";

import { connect } from "react-redux";
import { Dispatch } from "redux";

import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
} from "@fluentui/react/lib/DetailsList";
import { Text } from "@fluentui/react/lib/Text";

import * as EventActions from "../../Dataflows/Event/EventAction";

import { getSelectedEvent } from "../../Dataflows/Event/EventSelector";
import { AppState } from "../../Dataflows/Reducers";
import { IEventUserProfileRole } from "../../Interfaces/Event/IEventUserProfileRole";
import { IEventStudentListProps } from "./IEventStudentListProps";

const columns: IColumn[] = [
  {
    key: "userProfileId",
    name: "ID",
    ariaLabel: "Student ID",
    fieldName: "userProfileId",
    minWidth: 20,
    maxWidth: 36,
  },
  {
    key: "userProfileFirstName",
    name: "First Name",
    ariaLabel: "First name of student",
    fieldName: "userProfileFirstName",
    minWidth: 100,
    maxWidth: 150,
  },
  {
    key: "userProfileLastName",
    name: "Last Name",
    ariaLabel: "Last name of student",
    fieldName: "userProfileLastName",
    minWidth: 100,
    maxWidth: 150,
  },
  {
    key: "userProfilePrimaryEmail",
    name: "Primary Email",
    ariaLabel: "Primary Email of student",
    fieldName: "userProfilePrimaryEmail",
    minWidth: 100,
    maxWidth: 150,
  },
  {
    key: "userProfileStudentPartnerEmail",
    name: "Student Ambassadors Email",
    ariaLabel: "Student Ambassadors Email",
    fieldName: "userProfileStudentPartnerEmail",
    minWidth: 100,
    maxWidth: 150,
  },
  {
    key: "userProfileLevelStatus",
    name: "Milestone",
    ariaLabel: "Student's milestone",
    fieldName: "userProfileLevelStatus",
    minWidth: 150,
    maxWidth: 200,
  },
  {
    key: "eventRoleName",
    name: "Event Role Name",
    ariaLabel: "Event role of student",
    fieldName: "eventRoleName",
    minWidth: 100,
    maxWidth: 120,
  },
];

const EventStudentList: React.FunctionComponent<IEventStudentListProps> = (
  props: IEventStudentListProps
) => {
  const { selectedEvent } = props;
  const students = [...selectedEvent.eventUserProfileContributorRole];

  if (selectedEvent.eventOrganizerUserProfileRole) {
    students.push(selectedEvent.eventOrganizerUserProfileRole);
  }

  if (students.length === 0) {
    return <Text>No students connected to event.</Text>;
  }

  return (
    <DetailsList
      items={students}
      columns={columns}
      selectionMode={SelectionMode.none}
      getKey={(item: any): string => `${(item as IEventUserProfileRole).userProfileId}`}
      setKey="none"
      layoutMode={DetailsListLayoutMode.fixedColumns}
      isHeaderVisible={true}
      enterModalSelectionOnTouch={false}
    />
  );
};

/**
 * Maps StudentsContainer to redux state
 * @param {AppState} state The App state.
 */
const mapStateToProps = (state: AppState, ownProps: IEventStudentListProps & any) => ({
  selectedEvent: getSelectedEvent(state.eventState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (dispatch: Dispatch<EventActions.EventActionTypes>) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(EventStudentList);
